import { Connection, Provider } from "src/api/connectApi/types";
import {
  CONNECTIONS_MAP,
  providerInConfigMap,
  ProviderTag,
} from "src/connections/connectionConfigs";
import {
  isCaseCreationNodeConnection,
  isManualReviewNodeConnection,
} from "src/connections/model/inboundWebhook";
import { assertUnreachable } from "src/utils/typeUtils";

export const getProviderDescription = (connection: Connection): string => {
  const provider = connection.provider as Provider;
  if (providerInConfigMap(provider)) {
    return CONNECTIONS_MAP[provider]!.description;
  }

  switch (provider) {
    case "schufa": {
      return "Credit information in Germany since 1927";
    }
    case "boniversum": {
      return "Delivering B2C solvency information";
    }
    case "codat": {
      return "Connecting financial products for small businesses";
    }
    case "data_x": {
      return "Provides insights into a consumer's non-prime tradelines, payments, and inquiry activity not found in traditional credit files";
    }
    case "dnb": {
      return "Solutions that help you manage risk and compliance";
    }
    case "crif_b2b": {
      return "Supports companies and financial institutions in the management of their digital customer journey";
    }
    case "crif_b2c": {
      return "The end-to-end knowledge company";
    }
    case "crif_high_mark": {
      return "India's leading RBI-licensed credit bureau";
    }
    case "custom": {
      return "Add a Custom Connection";
    }
    case "equifax_uk": {
      return "Predictive consumer data to help make more profitable decisions";
    }
    case "equifax_us": {
      return "Make smarter, faster decisions with direct access to superior data and analytics";
    }
    case "equifax_us_commercial_risk": {
      return "Make smarter, faster decisions with direct access to superior data and analytics";
    }
    case "experian_us_b2b": {
      return "Data-driven solutions for every industry";
    }
    case "experian_us": {
      return "Global leader in consumer and business credit reporting and marketing services";
    }
    case "experian_us_clarity_services": {
      return "Experian's Clarity Services specializes in alternative financial services data and solutions";
    }
    case "creditsafe": {
      return "Understand the companies you do business with";
    }
    case "webhook": {
      if (isManualReviewNodeConnection(connection)) {
        return "Wait for human input";
      } else if (isCaseCreationNodeConnection(connection)) {
        return "Create a case";
      }
      return "Wait for an incoming webhook";
    }
    case "postgresql": {
      return "PostgreSQL";
    }
    case "bigquery": {
      return "BigQuery";
    }
    case "snowflake": {
      return "Snowflake";
    }
    case "redshift": {
      return "Redshift";
    }
    case "mysql": {
      return "MySQL";
    }
    case "retool": {
      return "Retool";
    }
    case "mongodb": {
      return "MongoDB";
    }
    default: {
      assertUnreachable(provider);
    }
  }
  return "";
};

export const getProviderName = (provider: Provider): string => {
  if (providerInConfigMap(provider)) {
    return CONNECTIONS_MAP[provider]!.name;
  }
  switch (provider) {
    case "schufa": {
      return "Schufa";
    }
    case "boniversum": {
      return "Boniversum";
    }
    case "custom": {
      return "Custom";
    }
    case "codat": {
      return "Codat";
    }
    case "data_x": {
      return "Equifax - DataX";
    }
    case "dnb": {
      return "Dun & Bradstreet";
    }
    case "crif_b2b": {
      return "CRIF - B2B";
    }
    case "crif_b2c": {
      return "CRIF - B2C";
    }
    case "crif_high_mark": {
      return "CRIF High Mark";
    }
    case "equifax_uk": {
      return "UK | Equifax";
    }
    case "equifax_us": {
      return "US | Equifax";
    }
    case "equifax_us_commercial_risk": {
      return "US | Equifax - Commercial Risk";
    }
    case "experian_us_b2b": {
      return "US | Experian - B2B";
    }
    case "experian_us": {
      return "US | Experian - B2C";
    }
    case "experian_us_clarity_services": {
      return "US | Experian - Clarity Services";
    }
    case "creditsafe": {
      return "Creditsafe - B2B";
    }
    case "webhook": {
      return "Inbound Webhook";
    }
    case "postgresql": {
      return "PostgreSQL";
    }
    case "bigquery": {
      return "BigQuery";
    }
    case "snowflake": {
      return "Snowflake";
    }
    case "redshift": {
      return "Redshift";
    }
    case "mysql": {
      return "MySQL";
    }
    case "retool": {
      return "Retool";
    }
    case "mongodb": {
      return "MongoDB";
    }
    default:
      assertUnreachable(provider);
      return "";
  }
};

export const getProviderTags = (provider: Provider): ProviderTag[] => {
  // boniversum, codat, creditsafe, crif_b2b, crif_b2c, dnb, equifax_uk, schufa
  // are not part of services/decide-ui/src/connections/connectionConfigs/
  switch (provider) {
    case "equifax_canada":
      return [
        { value: "Equifax", category: "provider" },
        { value: "Canada", category: "territory" },
      ];
    case "bkr":
      return [
        { value: "BKR", category: "provider" },
        { value: "Netherlands", category: "territory" },
      ];
    case "creditreform":
      return [
        { value: "Creditreform", category: "provider" },
        { value: "Germany", category: "territory" },
      ];
    case "experian_colombia":
      return [
        { value: "Experian", category: "provider" },
        { value: "Colombia", category: "territory" },
        { value: "DataCrédito", category: "product" },
      ];
    case "experian_germany":
      return [
        { value: "Experian", category: "provider" },
        { value: "Germany", category: "territory" },
      ];
    case "crif_high_mark":
      return [
        { value: "CRIF", category: "provider" },
        { value: "India", category: "territory" },
        { value: "Highmark", category: "product" },
      ];
    case "experian_us":
      return [
        { value: "Experian", category: "provider" },
        { value: "US", category: "territory" },
        { value: "B2C", category: "segment" },
      ];
    case "plaid":
      return [
        { value: "Plaid", category: "provider" },
        { value: ["Canada", "US", "UK"], category: "territory" },
      ];
    case "sardine":
      return [
        { value: "Sardine", category: "provider" },
        { value: "Global", category: "territory" },
      ];
    case "sentilink":
      return [
        { value: "SentiLink", category: "provider" },
        { value: "US", category: "territory" },
      ];
    case "socure":
      return [
        { value: "Socure", category: "provider" },
        { value: "US", category: "territory" },
      ];
    case "thomson_reuters":
      return [
        { value: "Thomson Reuters", category: "provider" },
        { value: "US", category: "territory" },
      ];
    case "trans_union":
      return [
        { value: "TransUnion", category: "provider" },
        { value: "US", category: "territory" },
      ];
    case "crs":
      return [
        { value: "CRS", category: "provider" },
        { value: "US", category: "territory" },
      ];
    case "schufa":
      return [
        { value: "Schufa", category: "provider" },
        { value: "Germany", category: "territory" },
      ];
    case "boniversum":
      return [
        { value: "Boniversum", category: "provider" },
        { value: "Germany", category: "territory" },
      ];
    case "codat":
      return [
        { value: "Codat", category: "provider" },
        { value: "Global", category: "territory" },
      ];
    case "dnb":
      return [
        { value: "Dun & Bradstreet", category: "provider" },
        { value: "Global", category: "territory" },
      ];
    case "crif_b2b":
      return [
        { value: "CRIF", category: "provider" },
        { value: "Global", category: "territory" },
        { value: "B2B", category: "segment" },
      ];
    case "crif_b2c":
      return [
        { value: "CRIF", category: "provider" },
        { value: "Global", category: "territory" },
        { value: "B2C", category: "segment" },
      ];
    case "equifax_uk":
      return [
        { value: "Equifax", category: "provider" },
        { value: "UK", category: "territory" },
      ];
    case "creditsafe":
      return [
        { value: "Creditsafe", category: "provider" },
        { value: "Global", category: "territory" },
      ];
    case "data_x":
      return [
        { value: "DataX", category: "provider" },
        { value: "Global", category: "territory" },
      ];
    case "equifax_us":
      return [
        { value: "Equifax", category: "provider" },
        { value: "US", category: "territory" },
      ];
    case "equifax_us_commercial_risk":
      return [
        { value: "Equifax", category: "provider" },
        { value: "US", category: "territory" },
        { value: "Commercial Risk", category: "product" },
      ];
    case "experian_us_b2b":
      return [
        { value: "Experian", category: "provider" },
        { value: "US", category: "territory" },
        { value: "B2B", category: "segment" },
      ];
    case "experian_us_clarity_services":
      return [
        { value: "Experian", category: "provider" },
        { value: "US", category: "territory" },
        { value: "Clarity Services", category: "product" },
      ];
    case "custom":
    case "webhook":
    case "postgresql":
    case "bigquery":
    case "snowflake":
    case "redshift":
    case "mysql":
    case "retool":
    case "mongodb":
      return [];
    default:
      return [];
  }
};
